import {
  action,
  computed,
  makeObservable,
  observable,
  onBecomeObserved,
  onBecomeUnobserved,
  runInAction,
} from "mobx";
import { liveQuery, type Subscription } from "dexie";

import { Maybe } from "@/domains/common/types";
import { Uuid } from "@/domains/global/identifiers";
import type { AudioRecordingObservable } from "@/store/audio-recordings";
import type { AppStoreAudioRecordingStore } from "@/store/audio-recordings/audio-recording.store";

type ConstructorArgs = {
  store: AppStoreAudioRecordingStore;
  noteId: Uuid | null;
};

export class NoteAudioRecordingObservable {
  private store: AppStoreAudioRecordingStore;
  private noteId: Uuid | null;
  private subscription?: Subscription;

  recordingId: Maybe<Uuid>;
  recordingIds: Uuid[];
  isLoading: boolean;

  constructor({ store, noteId }: ConstructorArgs) {
    this.store = store;
    this.noteId = noteId;
    this.recordingId = undefined;
    this.recordingIds = [];
    this.isLoading = true;

    makeObservable<this, "store" | "noteId" | "subscription">(this, {
      store: false,
      noteId: observable,
      subscription: observable,
      recordingId: observable,
      recordingIds: observable.shallow,
      isLoading: observable,
      recording: computed,
      recordings: computed,
      subscribe: action,
      unsubscribe: action,
      dispose: action,
    });

    onBecomeObserved(this, "recording", () => this.subscribe());
    onBecomeUnobserved(this, "recording", () => this.unsubscribe());
  }

  get recording(): Maybe<AudioRecordingObservable> {
    return this.recordingId ? this.store.get(this.recordingId) : undefined;
  }

  get recordings(): AudioRecordingObservable[] {
    return this.recordingIds
      .map(recordingId => this.store.get(recordingId))
      .filter((recording): recording is AudioRecordingObservable => recording !== undefined);
  }

  subscribe() {
    this.unsubscribe();

    runInAction(() => {
      this.isLoading = true;
    });

    const targetNoteId = this.noteId;

    if (!targetNoteId) {
      return;
    }

    this.subscription = liveQuery(async () => {
      const clientGroupId = await this.store.getLocalClientGroupId();

      return this.store.localTable
        .where("associated_note_id")
        .equals(targetNoteId)
        .filter(row => row.trashed_at === null)
        .filter(row => row.associated_client_id === clientGroupId)
        .sortBy("locally_created_at");
    }).subscribe({
      next: records => {
        runInAction(() => {
          this.recordingIds = records.map(record => record.model_id as Uuid);
          this.recordingId = this.recordingIds.at(-1);
          this.isLoading = false;
        });
      },
      error: () => {
        runInAction(() => {
          this.recordingId = undefined;
          this.recordingIds = [];
          this.isLoading = false;
        });
      },
    });
  }

  unsubscribe() {
    this.subscription?.unsubscribe();
    this.subscription = undefined;
  }

  dispose() {
    this.unsubscribe();
  }
}
